import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { SalaMultijugadorResponseDTO, EstadoSala } from '../types/multijugador';
import { MultijugadorService } from '../services/multijugadorService';
import { WebSocketService } from '../services/webSocketService';
import { authService } from '../services/authService';
import SalaEspera from '../components/multijugador/SalaEspera';
import RankingMultijugador from '../components/multijugador/RankingMultijugador';
import EsperandoResultados from '../components/multijugador/EsperandoResultados';

const SalaMultijugadorPage: React.FC = () => {
  console.log('🏠 [SALA] ====== COMPONENTE MONTADO ======');

  const { codigoSala } = useParams<{ codigoSala: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  console.log('📍 [SALA] Parámetros:', { codigoSala });
  console.log('📍 [SALA] Location state:', location.state);

  const [sala, setSala] = useState<SalaMultijugadorResponseDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [esperandoResultados, setEsperandoResultados] = useState(false);
  const [participantesTerminados, setParticipantesTerminados] = useState(0);
  const [primeraLlamada, setPrimeraLlamada] = useState(true);

  useEffect(() => {
    console.log('🏠 [SALA] useEffect ejecutándose');
    console.log('   - codigoSala:', codigoSala);
    console.log('   - location.state:', location.state);

    if (!codigoSala) {
      console.warn('⚠️ [SALA] No hay código de sala, navegando a /multijugador');
      navigate('/multijugador');
      return;
    }

    // Si viene de completar el examen, verificar si la sala ya finalizó
    if (location.state?.esperandoResultados) {
      console.log('⏳ [SALA] Usuario terminó examen, verificando estado de sala...');

      // Si la sala ya está FINALIZADA, no mostrar espera, ir directo al ranking
      if (location.state?.sala?.estado === EstadoSala.FINALIZADA) {
        console.log('🏆 [SALA] Sala ya finalizada, mostrando ranking directamente');
        setSala(location.state.sala);
        setEsperandoResultados(false);
        setLoading(false);
        return;
      }

      // Si la sala aún no finaliza, mostrar página de espera
      console.log('⏳ [SALA] Sala aún en progreso, mostrando página de espera');
      setEsperandoResultados(true);
      setParticipantesTerminados(location.state.participantesTerminados || 1);

      if (location.state?.sala) {
        setSala(location.state.sala);
        setLoading(false);
      } else if (codigoSala) {
        cargarSala();
      }
      return;
    }

    // Si viene del estado de navegación, usar esos datos
    if (location.state?.sala) {
      console.log('✅ [SALA] Usando sala del location.state:', location.state.sala);
      setSala(location.state.sala);
      setLoading(false);
    } else {
      console.log('🌐 [SALA] No hay sala en state, cargando desde servidor...');
      cargarSala();
    }
  }, [codigoSala, location.state]);

  // Efecto para escuchar eventos WebSocket cuando está esperando resultados
  useEffect(() => {
    if (!esperandoResultados || !codigoSala) return;

    console.log('🔌 [SALA] Conectando WebSocket para escuchar finalización de sala');
    const webSocketService = WebSocketService.getInstance();

    const setupWebSocket = async () => {
      try {
        await webSocketService.connect();

        // Suscribirse a eventos de la sala
        webSocketService.subscribe(`/topic/sala/${codigoSala}/finalizada`, () => {
          console.log('🏁 [SALA] Sala finalizada (WebSocket), redirigiendo a ranking');
          setEsperandoResultados(false);
          cargarSala(); // Recargar sala para obtener estado FINALIZADA
        });

        webSocketService.subscribe(`/topic/sala/${codigoSala}/ranking`, (data: any) => {
          console.log('📊 [SALA] Actualización de ranking:', data);
          if (data.participantesTerminados) {
            setParticipantesTerminados(data.participantesTerminados);
          }

          // Si todos terminaron, verificar estado de sala
          if (data.participantesTerminados && data.totalParticipantes &&
              data.participantesTerminados >= data.totalParticipantes) {
            console.log('🎯 [SALA] Todos terminaron según ranking - verificando estado...');
            setTimeout(() => {
              cargarSala(); // Recargar para verificar si cambió a FINALIZADA
            }, 2000); // Esperar 2 segundos para que el backend actualice
          }
        });
      } catch (error) {
        console.error('❌ [SALA] Error al conectar WebSocket:', error);
      }
    };

    setupWebSocket();

    // Polling de seguridad: verificar cada 5 segundos si la sala finalizó
    // Esto es un fallback en caso de que el WebSocket falle
    console.log('⏰ [SALA] Iniciando polling de verificación cada 5 segundos');
    const pollingInterval = setInterval(async () => {
      console.log('🔄 [SALA] Polling: Verificando estado de la sala...');
      try {
        const salaActualizada = await MultijugadorService.obtenerSala(codigoSala);
        console.log('📊 [SALA] Polling: Estado actual:', salaActualizada.estado);

        if (salaActualizada.estado === EstadoSala.FINALIZADA) {
          console.log('🏁 [SALA] Polling: Sala finalizada detectada - mostrando ranking');
          setSala(salaActualizada);
          setEsperandoResultados(false);
          clearInterval(pollingInterval); // Detener polling
        } else {
          // Actualizar contador de participantes terminados
          const terminados = salaActualizada.participantes.filter(p => p.finalizoExamen).length;
          console.log('📊 [SALA] Polling: Participantes terminados:', terminados);
          setParticipantesTerminados(terminados);
          setSala(salaActualizada);
        }
      } catch (error) {
        console.error('❌ [SALA] Error en polling:', error);
      }
    }, 5000); // Verificar cada 5 segundos

    return () => {
      console.log('🔌 [SALA] Desconectando WebSocket y deteniendo polling');
      webSocketService.unsubscribe(`/topic/sala/${codigoSala}/finalizada`);
      webSocketService.unsubscribe(`/topic/sala/${codigoSala}/ranking`);
      clearInterval(pollingInterval);
    };
  }, [esperandoResultados, codigoSala]);

  // Efecto para redirigir automáticamente si la sala está en progreso
  useEffect(() => {
    // NO redirigir si el usuario ya terminó y está esperando resultados
    if (esperandoResultados) {
      console.log('⏳ [SALA] Usuario esperando resultados - NO redirigir a examen');
      return;
    }

    if (sala && sala.estado === EstadoSala.EN_PROGRESO) {
      // Verificar si el usuario actual ya terminó el examen
      const currentUser = authService.getCurrentUserFromStorage();
      if (currentUser && sala.participantes) {
        const participanteActual = sala.participantes.find(p => p.usuarioId === currentUser.id);

        if (participanteActual?.finalizoExamen) {
          console.log('✅ [SALA] Usuario ya terminó el examen - NO redirigir');
          // Usuario ya terminó, mostrar pantalla de espera
          setEsperandoResultados(true);
          const terminados = sala.participantes.filter(p => p.finalizoExamen).length;
          setParticipantesTerminados(terminados);
          return;
        }
      }

      console.log('⚡ [SALA] Examen en progreso - redirigiendo a examen...');
      const timer = setTimeout(() => {
        handleInicioExamen();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [sala?.estado, esperandoResultados]);

  const cargarSala = async () => {
    if (!codigoSala) {
      console.error('❌ [SALA] No hay codigoSala para cargar');
      return;
    }

    console.log('🌐 [SALA] Cargando sala desde servidor:', codigoSala);
    try {
      setLoading(true);
      setError(null);
      const salaData = await MultijugadorService.obtenerSala(codigoSala);
      console.log('✅ [SALA] Sala cargada:', salaData);
      console.log('   - Estado:', salaData.estado);
      console.log('   - Participantes:', salaData.participantes.length);
      setSala(salaData);
      setPrimeraLlamada(false); // Marcar que ya se cargó exitosamente
    } catch (error: any) {
      console.error('❌ [SALA] Error al cargar sala:', error);
      console.error('   - Mensaje:', error.message);
      console.error('   - Response:', error.response?.data);
      setError(error.message);

      // NO mostrar notificaciones durante la primera carga
      if (!primeraLlamada) {
        // Solo mostrar notificación si NO es la primera carga
        const esErrorDeRed = error.message?.includes('Network') ||
                            error.message?.includes('timeout') ||
                            error.code === 'ECONNABORTED' ||
                            error.response?.status === 503 ||
                            error.response?.status === 504;

        if (!esErrorDeRed) {
          notifications.show({
            title: 'Error al cargar sala',
            message: error.message,
            color: 'red',
          });

          console.log('🔄 [SALA] Redirigiendo a /multijugador en 3 segundos...');
          // Redirigir a la página principal después de un error
          setTimeout(() => {
            navigate('/multijugador');
          }, 3000);
        } else {
          console.warn('⚠️ [SALA] Error de red temporal - no se muestra notificación');
        }
      } else {
        console.warn('⚠️ [SALA] Primera carga - errores no se muestran al usuario');
      }
    } finally {
      setPrimeraLlamada(false); // Marcar que ya no es la primera llamada
      setLoading(false);
    }
  };

  const handleSalaActualizada = (salaActualizada: SalaMultijugadorResponseDTO) => {
    console.log('🔄 [SALA] Sala actualizada:', salaActualizada);
    console.log('   - Estado nuevo:', salaActualizada.estado);
    setSala(salaActualizada);
  };

  const handleInicioExamen = async () => {
    console.log('🚀 [SALA] ====== INICIO DE EXAMEN ======');
    if (!sala) {
      console.error('❌ [SALA] No hay sala para iniciar examen');
      return;
    }

    console.log('📊 [SALA] Datos de la sala actual:', sala);
    console.log('🔄 [SALA] Recargando sala desde el backend para obtener intentoExamenId...');

    try {
      // Recargar la sala desde el backend para obtener los intentoExamenId actualizados
      const salaActualizada = await MultijugadorService.obtenerSala(sala.codigoSala);
      console.log('✅ [SALA] Sala actualizada obtenida:', salaActualizada);
      console.log('   - Participantes con intentoExamenId:', salaActualizada.participantes.map(p => ({
        usuario: p.nombreUsuario,
        intentoExamenId: p.intentoExamenId
      })));

      setSala(salaActualizada);

      console.log('🔄 [SALA] Navegando a /examen/multijugador');
      console.log('   - State:', {
        sala: salaActualizada,
        tipoExamen: 'MULTIJUGADOR',
        fromMultiplayer: true
      });

      // Navegar al examen con la sala actualizada que tiene los intentoExamenId
      navigate('/examen/multijugador', {
        state: {
          sala: salaActualizada,
          tipoExamen: 'MULTIJUGADOR',
          fromMultiplayer: true
        }
      });
      console.log('✅ [SALA] Navegación iniciada con sala actualizada');
    } catch (error: any) {
      console.error('❌ [SALA] Error al recargar sala antes de iniciar examen:', error);
      notifications.show({
        title: 'Error',
        message: 'No se pudo cargar los datos del examen. Intenta de nuevo.',
        color: 'red',
      });
    }
  };

  const handleExamenTerminado = () => {
    if (!sala) return;

    // Navegar al ranking
    navigate(`/multijugador/sala/${sala.codigoSala}/ranking`, {
      state: { sala }
    });
  };

  console.log('🖼️ [SALA] Renderizando - Estado actual:', { loading, error, sala: sala?.estado });

  if (loading) {
    console.log('⏳ [SALA] Mostrando loader');
    return (
      <Container size="lg" py="xl">
        <div className="flex justify-center items-center h-64">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text>Cargando sala...</Text>
          </Stack>
        </div>
      </Container>
    );
  }

  if (error || !sala) {
    console.error('❌ [SALA] Mostrando error o sala no encontrada');
    console.error('   - Error:', error);
    console.error('   - Sala:', sala);
    return (
      <Container size="lg" py="xl">
        <div className="flex justify-center items-center h-64">
          <Stack align="center" gap="md">
            <Text color="red" size="lg">
              {error || 'Sala no encontrada'}
            </Text>
            <Text color="dimmed" size="sm">
              Redirigiendo...
            </Text>
          </Stack>
        </div>
      </Container>
    );
  }

  // Si está esperando resultados, mostrar página de espera
  if (esperandoResultados) {
    console.log('⏳ [SALA] Mostrando página de espera de resultados');
    console.log('   - Participantes terminados:', participantesTerminados);
    console.log('   - Total participantes:', sala.cantidadParticipantes);
    return (
      <EsperandoResultados
        sala={sala}
        participantesTerminados={participantesTerminados}
        totalParticipantes={sala.cantidadParticipantes}
      />
    );
  }

  // Determinar qué componente mostrar según el estado
  console.log('🎭 [SALA] Determinando componente según estado:', sala.estado);

  switch (sala.estado) {
    case EstadoSala.ESPERANDO:
      console.log('⏰ [SALA] Renderizando SalaEspera');
      return (
        <SalaEspera
          sala={sala}
          onSalaActualizada={handleSalaActualizada}
          onInicioExamen={handleInicioExamen}
        />
      );

    case EstadoSala.EN_PROGRESO:
      // Si el examen está en progreso, mostrar loader mientras redirige
      return (
        <Container size="lg" py="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="lg" fw={500}>
              El examen está en progreso...
            </Text>
            <Text color="dimmed" size="sm">
              Redirigiendo al examen...
            </Text>
          </Stack>
        </Container>
      );

    case EstadoSala.FINALIZADA:
      return (
        <RankingMultijugador
          codigoSala={sala.codigoSala}
          sala={sala}
        />
      );

    case EstadoSala.CANCELADA:
      return (
        <Container size="lg" py="xl">
          <Stack align="center" gap="md">
            <Text color="red" size="lg" fw={500}>
              Esta sala ha sido cancelada
            </Text>
            <Text color="dimmed" size="sm">
              Redirigiendo al menú principal...
            </Text>
          </Stack>
        </Container>
      );

    default:
      return (
        <Container size="lg" py="xl">
          <Stack align="center" gap="md">
            <Text color="orange" size="lg">
              Estado de sala desconocido
            </Text>
          </Stack>
        </Container>
      );
  }
};

export default SalaMultijugadorPage;